import React, { useEffect } from "react";
import TopBanner from "../components/TopBanner";
import { ContentWrapper } from "./InsuranceGuidance";
import AssociateForm from "../components/AssociateForm";
import StepsTimeline from "../components/StepsTimeline";
import { Card, Cards, Icon } from "../components/WhoWeAre";
import { CardText, CardTitle } from "../components/WhyChooseUS";
import styled from "styled-components";

const Advisors = ({ openModal }) => {
  useEffect(() => {
    document.title =
      "For Insurance Advisors – Grow Your Insurance Business with Wealthies | Wealthies";
  }, []);

  const stepsData = [
    {
      title: "Step 1 – Advisor Registration",
      desc: "Insurance professionals can register on the Wealthies platform by providing their professional details and licensing information.",
    },
    {
      title: "Step 2 – Customer Inquiry Generation",
      desc: "Individuals visiting the Wealthies platform may submit inquiries when they seek assistance understanding insurance coverage options.",
    },
    {
      title: "Step 3 – Connect with Customers",
      desc: "Registered advisors may receive opportunities to connect with these individuals and provide professional assistance based on their requirements.",
    },
    {
      title: "Step 4 – Continue Client Support",
      desc: "Advisors can continue assisting customers with insurance policies and provide professional services as per their licensing and insurer guidelines.",
    },
  ];

  return (
    <>
      <TopBanner content={"For Insurance Advisors"} />
      <ContentWrapper>
        <div className="text-content">
          <h2>
            Grow Your <span>Insurance Business</span> with Quality Customer
            Opportunities
          </h2>
          <p>
            Building a successful insurance practice often requires consistent
            customer interactions and the ability to connect with individuals
            who are actively looking for insurance information.
          </p>
          <p>
            Wealthies provides a platform that helps insurance professionals
            connect with individuals seeking guidance about insurance products.
          </p>
          <p>
            Through our platform, licensed insurance advisors may receive
            customer inquiries and opportunities to assist individuals who are
            exploring insurance coverage options.
          </p>

          <h2>
            Why <span>Advisors</span> Join Wealthies
          </h2>
          <p>
            Insurance professionals often face challenges in reaching potential
            customers who are genuinely interested in learning about insurance.
          </p>
          <p>
            Wealthies aims to simplify this process by helping advisors connect
            with individuals who are actively exploring insurance solutions.
          </p>
          <p>Some reasons advisors choose to work with Wealthies include:</p>
          <ul>
            <p>
              ✔ Access to customer inquiries from individuals seeking insurance
              guidance
            </p>
            <p>
              ✔ Opportunity to connect with informed customers who have already
              explored insurance basics
            </p>
            <p>
              ✔ Platform support designed to help advisors interact with
              potential clients
            </p>
            <p>✔ Transparent communication between customers and advisors</p>
          </ul>

          <h2>
            How <span>Wealthies</span> Works for Advisors
          </h2>
          <StepsTimeline steps={stepsData} />

          <h2>Who Can Join</h2>
          <p>
            Wealthies is designed for licensed insurance professionals who want
            to connect with customers seeking insurance information.
          </p>
          <p>Advisors who may benefit from joining include:</p>
          <ul>
            <li>licensed insurance agents</li>
            <li>financial professionals involved in insurance distribution</li>
            <li>advisors looking to expand their client base</li>
          </ul>
          <p>
            Advisors must comply with applicable regulatory and licensing
            requirements.
          </p>

          <h2>
            Benefits of <span>Joining Wealthies</span>
          </h2>
          <Cards>
            <Card>
              <Icon className="fas fa-user-plus"></Icon>
              <CardTitle>Access to Customer Opportunities</CardTitle>
              <CardText>
                Advisors may receive opportunities to interact with individuals
                seeking insurance guidance.
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-lightbulb"></Icon>
              <CardTitle>Informed Customers</CardTitle>
              <CardText>
                Many individuals visiting Wealthies explore insurance guides
                before submitting inquiries, helping them better understand
                their needs before speaking with an advisor.
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-network-wired"></Icon>
              <CardTitle>Platform Support</CardTitle>
              <CardText>
                Wealthies provides a structured platform that helps facilitate
                communication between customers and advisors.
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-handshake"></Icon>
              <CardTitle>Long-Term Relationship Building</CardTitle>
              <CardText>
                Advisors can develop ongoing relationships with customers by
                providing professional assistance and support.
              </CardText>
            </Card>
          </Cards>
          <h2>Advisor Registration</h2>
          <p>
            Interested insurance professionals can apply to join the Wealthies
            platform by submitting their details.
          </p>
          <p>Registration may require information such as:</p>
          <ul>
            <li>Name</li>
            <li>Contact information</li>
            <li>Insurance license details</li>
            <li>Experience in insurance advisory</li>
            <li>Area of specialization</li>
          </ul>
          <p style={{ color: "#005f73" }}>
            <strong>
              Registrations are limited to ensure quality interactions.
            </strong>
          </p>

          <AssociateForm />

          <h2 style={{ marginTop: "20px" }}>Professional Responsibility</h2>
          <p>
            Insurance advisors interacting with customers through the platform
            are responsible for providing professional services in accordance
            with their licensing requirements and insurer guidelines.
          </p>
          <p>
            Advisors should ensure that all policy recommendations and
            transactions comply with applicable regulatory requirements.
          </p>

          <p>
            Want to expand your insurance practice and connect with potential
            customers?
          </p>
          <p>
            Join Wealthies and start connecting with individuals seeking
            insurance guidance.
          </p>
          <ImportantNote>
            <NoteIcon>i</NoteIcon>
            <NoteText>
              By completing registration or purchasing services on{" "}
              <strong>Wealthies</strong>, you acknowledge and agree to the
              platform’s
              <span> Terms & Conditions</span> and <span>Refund Policy</span>.
            </NoteText>
          </ImportantNote>
        </div>
      </ContentWrapper>
    </>
  );
};

export default Advisors;

export const NoteIcon = styled.div`
  min-width: 32px;
  height: 32px;

  display: flex;
  align-items: center;
  justify-content: center;

  background: linear-gradient(135deg, #00c853, #2e7d32);
  color: #fff;

  border-radius: 50%;
  font-size: 14px;
  font-weight: bold;

  box-shadow: 0 6px 16px rgba(0, 200, 83, 0.3);
`;

export const NoteText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.6;
  color: #444;

  strong {
    color: #1a1a1a;
    font-weight: 600;
  }

  span {
    color: #2e7d32;
    font-weight: 500;
  }
`;
export const ImportantNote = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;

  margin-top: 20px;
  margin-bottom: 40px;
  padding: 16px 18px;

  border-radius: 12px;

  background: linear-gradient(
    135deg,
    rgba(0, 200, 83, 0.08),
    rgba(46, 125, 50, 0.05)
  );

  border: 1px solid rgba(0, 200, 83, 0.15);

  backdrop-filter: blur(6px);

  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.04);

  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 30px rgba(0, 200, 83, 0.12);
  }
`;
